import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Eye, EyeOff, LogIn, Coffee } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const LoginContainer = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%); padding: 20px;
`;

const LoginCard = styled(motion.div)`
  background: #111111; border: 1px solid #333333; border-radius: 20px; padding: 40px; width: 100%; max-width: 400px; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  @media (max-width: 480px) { padding: 30px 20px; border-radius: 16px; }
`;

const Logo = styled.div`
  text-align: center; margin-bottom: 32px;
  h1 { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; font-size: 2rem; font-weight: 700; font-family: var(--font-heading); margin-bottom: 8px; @media (max-width: 480px){ font-size: 1.75rem; } }
  p { color: #cccccc; font-size: 0.875rem; font-family: var(--font-body); }
`;

const Form = styled.form`display: flex; flex-direction: column; gap: 20px;`;
const FormGroup = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const Label = styled.label`color: #ffffff; font-weight: 500; font-size: 0.875rem; font-family: var(--font-body);`;

const Input = styled.input`
  padding: 12px 16px; border: 2px solid #333333; border-radius: 8px; background: #111111; color: #ffffff; font-size: 16px; font-family: var(--font-body); transition: border-color 0.3s ease; width: 100%; box-sizing: border-box;
  &:focus { outline: none; border-color: #e0c16f; box-shadow: 0 0 0 3px rgba(224, 193, 111, 0.1); }
  &::placeholder { color: #666666; }
`;

const PasswordInput = styled.div`position: relative; input { padding-right: 48px; }`;
const PasswordToggle = styled.button`position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #cccccc; cursor: pointer; padding: 4px; &:hover { color: #ffffff; }`;

const SubmitButton = styled.button`
  padding: 16px 24px; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000000; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; gap: 8px;
  @media (max-width: 480px) { padding: 14px 20px; font-size: 15px; }
  &:hover { background: linear-gradient(95.36deg, #bd851b 5.18%, #e0c16f 93.21%); transform: translateY(-2px); box-shadow: 0 4px 15px rgba(224, 193, 111, 0.3); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const Divider = styled.div`text-align: center; margin: 24px 0; position: relative; &::before { content: ''; position: absolute; top: 50%; left: 0; right: 0; height: 1px; background: #333333; } span { background: #111111; padding: 0 16px; color: #cccccc; font-size: 0.875rem; position: relative; z-index: 1; }`;
const SignupLink = styled.div`text-align: center; color: #cccccc; font-size: 0.875rem; a { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; text-decoration: none; font-weight: 500; &:hover { text-decoration: underline; } }`;
const ForgotLink = styled(Link)`align-self:flex-end; color:#e0c16f; font-size:.875rem; text-decoration:none; margin-top:-8px; &:hover{text-decoration:underline;}`;
const ErrorMessage = styled.div`color: #ff6b6b; font-size: 0.875rem; text-align: center;`;

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true); setError('');
    try {
      const result = await login(data.email, data.password);
      if (result.success) {
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        setError(result.error);
        toast.error(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Logo>
          <h1><Coffee size={32} style={{ marginRight: '8px', verticalAlign: 'middle' }} /> BUCX</h1>
          <p>Welcome back to your creator platform</p>
        </Logo>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" placeholder="Enter your email" {...register('email', { required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' } })} />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <PasswordInput>
              <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })} />
              <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggle>
            </PasswordInput>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </FormGroup>

          <ForgotLink to="/forgot-password">Forgot password?</ForgotLink>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Signing in...' : <><LogIn size={20} /> Sign In</>}
          </SubmitButton>
        </Form>

        <Divider><span>or</span></Divider>
        <SignupLink>Don't have an account?{' '}<Link to="/signup">Sign up here</Link></SignupLink>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;
